import { format as formatNumber } from 'd3-format';
import { getDate, getMonth, intervalToDuration, parseISO } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';

export type BarChartData = [date: string, data: number][];

export type DotChartData = [
  startDate: string,
  endDate: string,
  dataValue: string,
  eventName: string,
  registrantsCount: number,
  attendeesCount: number,
][];

export const convertChartDataToTimezone = (
  data: BarChartData,
  timezone?: string,
): BarChartData => {
  if (!data || !timezone) return [];

  return data.map(([date, data]) => {
    const timezonedDate = utcToZonedTime(date, timezone);
    const formatted = format(timezonedDate, 'yyyy-MM-dd HH:mm:ss');

    return [formatted, data];
  });
};

export const getUpperBound = (maxValue: number, maxTicks: number) => {
  // make upper yAxis upper bound easily divisible
  // so that yAxis ticks are easy to read.
  // If upper bound is less than max num ticks,
  // there will be a tick for each number so divisibility isn't a concern

  const upperBound = maxValue;
  if (upperBound > 200000) {
    return roundToNextMultiple(upperBound, 100000);
  } else if (upperBound > 100000) {
    return roundToNextMultiple(upperBound, 50000);
  } else if (upperBound > 50000) {
    return roundToNextMultiple(upperBound, 20000);
  } else if (upperBound > 20000) {
    return roundToNextMultiple(upperBound, 15000);
  } else if (upperBound > 10000) {
    return roundToNextMultiple(upperBound, 5000);
  } else if (upperBound > 4000) {
    return roundToNextMultiple(upperBound, 2500);
  } else if (upperBound > 2000) {
    return roundToNextMultiple(upperBound, 1000);
  } else if (upperBound > 1000) {
    return roundToNextMultiple(upperBound, 500);
  } else if (upperBound > 200) {
    return roundToNextMultiple(upperBound, 100);
  } else if (upperBound > 100) {
    return roundToNextMultiple(upperBound, 50);
  } else if (upperBound > 20) {
    return roundToNextMultiple(upperBound, 25);
  } else if (upperBound > maxTicks) {
    return roundToNextMultiple(upperBound, 5);
  } else {
    return upperBound;
  }
};

const roundToNextMultiple = (x: number, multiple: number) => {
  return Math.ceil(x / multiple) * multiple;
};

export const numberFormatter = formatNumber('~s');

export const getMaxTickWidth = (ticks: (number | { valueOf(): number })[]) => {
  if (!ticks.length) return 0;

  const tickLengths = ticks.map(
    // return length of string excluding periods
    tick => numberFormatter(tick).replace(/\./g, '').length,
  );
  const longestTickLength = Math.max(...tickLengths);

  // assumes that on average chars are 7 pixels wide
  return longestTickLength * 7;
};

type Unit = 'minute' | 'hour' | 'day';

export const getIntervalUnit = (
  data: BarChartData | DotChartData,
): Unit | undefined => {
  if (!data || data.length < 2) return 'minute';

  const duration = intervalToDuration({
    start: parseISO(data[0][0]),
    end: parseISO(data[1][0]),
  });

  if (duration.days) {
    return 'day';
  } else if (duration.hours) {
    return 'hour';
  } else if (duration.minutes) {
    return 'minute';
  } else {
    console.error('Not a valid duration');
  }
  return undefined;
};

const getTimeLabelSubText = (
  date: Date,
  tickIndex: number,
  allTicks: string[],
  unit: string,
): string => {
  const isFirstTick = tickIndex === 0;
  const isDayUnit = unit === 'day';
  const dayOfMonth = getDate(date);
  const month = getMonth(date);
  const prevTickDate = parseISO(allTicks[tickIndex - 1]);
  const prevTickDayOfMonth = isFirstTick ? null : getDate(prevTickDate);
  const prevTickMonth = isFirstTick ? null : getMonth(prevTickDate);
  const isNextDay = prevTickDayOfMonth !== dayOfMonth;
  const isNextMonth = prevTickMonth !== month;
  const shouldShowMonth = isDayUnit && (isFirstTick || isNextMonth);
  const shouldShowMonthDay = !isDayUnit && (isFirstTick || isNextDay);

  let subText = '';
  if (shouldShowMonth) {
    subText = format(date, 'MMM');
  } else if (shouldShowMonthDay) {
    subText = format(date, 'MMM d');
  }

  return subText;
};

export const formatAxisLabel = (
  allTicks: string[],
  dateString?: string,
  unit?: Unit,
): { text: string; subText: string } => {
  const axisLabelFormats: { [key in Unit]: string } = {
    minute: 'h:mm a',
    hour: 'h:mm a',
    day: 'dd',
  };

  if (!dateString || !unit) return { text: '', subText: '' };

  const tickIndex = allTicks.indexOf(dateString);
  const date = parseISO(allTicks[tickIndex]);
  const timeFormat = axisLabelFormats[unit];

  return {
    text: format(date, timeFormat).replace(':00', ''),
    subText: getTimeLabelSubText(date, tickIndex, allTicks, unit),
  };
};

export const formatTooltipLabel = (dateString: string, unit?: Unit) => {
  const tooltipFormats: { [key in Unit]: string } = {
    minute: 'MMM dd, h:mm a',
    hour: 'MMM dd, h:mm a',
    day: 'MMM dd',
  };

  if (!dateString || !unit) return '';

  const date = parseISO(dateString);
  const timeFormat = tooltipFormats[unit];

  return format(date, timeFormat);
};
